import type { ReleaseArch, ReleaseOs, ReleaseVariant } from '@/types/models';

/** Extensions explicitly rejected when publishing audit toolkit binaries. */
const BLOCKED_EXTENSIONS = [
  '.zip', '.tar', '.gz', '.tgz', '.bz2', '.xz', '.7z',
  '.pdf', '.txt', '.md', '.json', '.xml', '.html', '.htm',
  '.js', '.ts', '.tsx', '.jsx', '.py', '.sh', '.ps1', '.bat', '.cmd',
  '.dll', '.msi', '.msm', '.msp', '.dmg', '.pkg', '.deb', '.rpm', '.app',
] as const;

const UNIX_EXECUTABLE_MIME_TYPES = new Set([
  'application/x-executable',
  'application/x-mach-binary',
  'application/x-elf',
  'application/x-sharedlib',
  'application/octet-stream',
]);

/**
 * Do not set `<input accept>` for release binaries: on macOS/Chrome it greys out
 * extensionless Unix executables even with `/*`. Gate uploads with
 * {@link isAllowedReleaseBinary} instead.
 */

export function getReleaseBinaryContentType(fileName: string): string {
  if (fileName.toLowerCase().endsWith('.exe')) {
    return 'application/vnd.microsoft.portable-executable';
  }
  return 'application/octet-stream';
}

/** Windows `.exe` or Unix executable (typically no extension, e.g. `auditai-macos`). */
export function isAllowedReleaseBinary(file: File): boolean {
  const name = file.name.trim();
  if (!name) return false;

  const lower = name.toLowerCase();
  if (lower.endsWith('.exe')) return true;
  if (BLOCKED_EXTENSIONS.some(ext => lower.endsWith(ext))) return false;

  const lastDot = name.lastIndexOf('.');
  // No extension, or leading dot only (hidden unix-style names)
  if (lastDot <= 0) return true;

  // Rare case: extension present but OS reports a Unix/binary MIME type
  return Boolean(file.type && UNIX_EXECUTABLE_MIME_TYPES.has(file.type));
}

// ── Nommage des binaires par OS / architecture / variante ────────────────────

const FILE_NAMES: Record<string, string> = {
  'windows/x86_64/onefile': 'auditai-windows.exe',
  'macos/arm64/onefile':    'auditai-macos',
  'macos/x86_64/onefile':   'auditai-macos-intel',
  'linux/x86_64/onefile':   'auditai-linux',
  'linux/arm64/onefile':    'auditai-linux-arm64',
  'linux/x86_64/onedir':    'auditai-linux-onedir.tar.gz',
};

/** Clé `os/arch/variant` d'une release (défauts des docs legacy : x86_64 / onefile). */
export function releaseKey(os: ReleaseOs, arch: ReleaseArch = 'x86_64', variant: ReleaseVariant = 'onefile'): string {
  return `${os}/${arch}/${variant}`;
}

/** Nom de fichier publié pour une combinaison ; `null` si la combinaison n'est pas distribuée. */
export function releaseFileName(os: ReleaseOs, arch: ReleaseArch = 'x86_64', variant: ReleaseVariant = 'onefile'): string | null {
  const effective = arch === 'universal' ? (os === 'macos' ? 'arm64' : 'x86_64') : arch;
  return FILE_NAMES[releaseKey(os, effective, variant)] ?? null;
}

/** Identifiant de document : `{version}-{os}-{arch}[-{variant}]` (variante omise pour onefile). */
export function releaseId(version: string, os: ReleaseOs, arch: ReleaseArch, variant: ReleaseVariant = 'onefile'): string {
  return `${version}-${os}-${arch}${variant === 'onefile' ? '' : `-${variant}`}`;
}

/**
 * Release à proposer pour `os`/`arch` : correspondance exacte, sinon binaire
 * `universal`, sinon `null`. Variante onefile uniquement (onedir = avancé).
 */
export function pickRelease<R extends { os: ReleaseOs; arch?: ReleaseArch; variant?: ReleaseVariant }>(
  releases: Iterable<R>, os: ReleaseOs, arch: ReleaseArch,
): R | null {
  let universal: R | null = null;
  for (const r of releases) {
    if (r.os !== os || (r.variant ?? 'onefile') !== 'onefile') continue;
    const rArch = r.arch ?? 'x86_64';
    if (rArch === arch) return r;
    if (rArch === 'universal' && !universal) universal = r;
  }
  return universal;
}

/** Commande locale de vérification SHA-256 du binaire téléchargé. */
export function sha256Command(os: ReleaseOs, fileName: string): string {
  if (os === 'windows') return `certutil -hashfile .\\${fileName} SHA256`;
  if (os === 'macos') return `shasum -a 256 ./${fileName}`;
  return `sha256sum ./${fileName}`;
}
